import { Component, EventEmitter, Input, Output, computed } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AlertComponent, ModalComponent, TableComponent } from '@tecalisdev/infinity';
import { OPERATION_STATUS } from '../../../../../models/operations.models';
import { NOT_AVAILABLE_DISPLAY, isNotAvailableDisplay } from '../../../../../../core/utils/utils';

interface IIrpfCompareRow {
	clickValue: string;
	notification: boolean;
	cells: { label: string }[];
}

@Component({
	selector: 'app-operation-detail-tabs-checks-financial-evaluation-irpf-modal',
	imports: [TranslatePipe, ModalComponent, AlertComponent, TableComponent],
	templateUrl: './irpf-modal.component.html',
	styleUrl: './irpf-modal.component.scss',
})
export class OperationDetailTabsChecksFinancialEvaluationIrpfModalComponent {
	public OPERATION_STATUS = OPERATION_STATUS;

	@Input() isVisible = false;
	@Input() status?: OPERATION_STATUS;
	@Input() detailsPayload: unknown;
	@Output() closeEvent = new EventEmitter<void>();

	public rows = computed<IIrpfCompareRow[]>(() => this.buildRows(this.detailsPayload));
	public hasMismatch = computed(() => this.rows().some((row) => row.notification));

	public close(): void {
		this.closeEvent.emit();
	}

	private buildRows(payload: unknown): IIrpfCompareRow[] {
		const parsed = this.parseUnknown(payload);
		if (!parsed || typeof parsed !== 'object') return [];
		const obj = parsed as Record<string, unknown>;
		const rows: IIrpfCompareRow[] = [];
		let index = 0;

		const pushRow = (type: string, documentValue: unknown, dataValue: unknown): void => {
			const documentText = this.stringifySimple(documentValue) || NOT_AVAILABLE_DISPLAY;
			const dataText = this.stringifySimple(dataValue) || NOT_AVAILABLE_DISPLAY;
			rows.push({
				clickValue: String(index++),
				notification: this.isMismatch(documentText, dataText),
				cells: [{ label: type || NOT_AVAILABLE_DISPLAY }, { label: documentText }, { label: dataText }],
			});
		};

		if (Array.isArray(obj['comparisons'])) {
			for (const entry of obj['comparisons']) {
				if (!entry || typeof entry !== 'object') continue;
				const cmp = entry as Record<string, unknown>;
				pushRow(
					this.stringifySimple(cmp['type']) || this.stringifySimple(cmp['field']) || 'IRPF',
					cmp['document'] ?? cmp['source'] ?? cmp['left'] ?? cmp['value'],
					cmp['data'] ?? cmp['registry'] ?? cmp['expected'] ?? cmp['right'] ?? cmp['reference'],
				);
			}
		}

		if (!rows.length) {
			for (const [key, value] of Object.entries(obj)) {
				if (!value || typeof value !== 'object') continue;
				const record = value as Record<string, unknown>;
				const hasComparable =
					record['document'] !== undefined ||
					record['data'] !== undefined ||
					record['registry'] !== undefined ||
					record['expected'] !== undefined ||
					record['source'] !== undefined ||
					record['reference'] !== undefined;

				if (hasComparable) {
					pushRow(
						key.toUpperCase(),
						record['document'] ?? record['source'] ?? record['value'],
						record['data'] ?? record['registry'] ?? record['expected'] ?? record['reference'],
					);
				}
			}
		}

		return rows;
	}

	private parseUnknown(value: unknown): unknown {
		if (typeof value === 'string') {
			try {
				return JSON.parse(value) as unknown;
			} catch {
				return null;
			}
		}
		return value;
	}

	private stringifySimple(value: unknown): string {
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			return String(value);
		}
		return '';
	}

	private isMismatch(documentText: string, dataText: string): boolean {
		if (isNotAvailableDisplay(documentText) || isNotAvailableDisplay(dataText)) return false;
		return documentText.trim().toLowerCase() !== dataText.trim().toLowerCase();
	}
}
